import React, {createContext, useEffect, useState} from 'react';
import axios from '../axiosConfig';

const ConfigLinksDetailsContext = createContext();

const ConfigLinksDetailsProvider = ({ children }) => {
  const [configLinks, setConfigLinks] = useState({});
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);
  const [configFetchedAt, setConfigFetchedAt] = useState(null);

  const CACHE_KEY = 'emun_config_links_v1';

  const saveCache = (data) => {
    try {
      const payload = { data, fetchedAt: Date.now() };
      localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
      setConfigFetchedAt(payload.fetchedAt);
    } catch (e) {
      // ignore localStorage errors
      console.warn('Failed to save config cache', e);
    }
  };

  const loadCache = () => {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.data) {
        setConfigFetchedAt(parsed.fetchedAt || null);
        return parsed.data;
      }
      return null;
    } catch (e) {
      return null;
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await axios.get('/settings');
      const configData = response?.data?.dataItem || response?.data || response;

      if (configData && Object.keys(configData).length > 0) {
        setConfigLinks(configData);
        saveCache(configData);

        // Update favicon to the uploaded municipality logo and bust browser cache.
        if (configData?.logoUrl) {
          const iconHref = `/${configData.logoUrl}?v=${Date.now()}`;
          document
            .querySelectorAll("link[rel='icon'], link[rel='apple-touch-icon']")
            .forEach((link) => {
              link.href = iconHref;
            });
        }
      }
    } catch (err) {
      console.warn('Failed to fetch config from server, will use cached config if available', err);
      // On failure, try to use cache
      const cached = loadCache();
      if (cached) {
        setConfigLinks(cached);
      }
    } finally {
      setIsConfigLoaded(true);
    }
  };

  useEffect(() => {
    // Load cache first (if any) to avoid flashing empty UI while fetching
    const cached = loadCache();
    if (cached) {
      setConfigLinks(cached);
    }

    // Always try to refresh from server; if succeeds it will overwrite cache
    fetchConfig();
  }, []);

  // Expose a manual refresh function
  const refreshConfig = async () => {
    setIsConfigLoaded(false);
    await fetchConfig();
    setIsConfigLoaded(true);
  };
  return (
    <ConfigLinksDetailsContext.Provider value={{ configLinks, setConfigLinks, isConfigLoaded, refreshConfig, configFetchedAt }}>
      {children}
    </ConfigLinksDetailsContext.Provider>
  );
};

export { ConfigLinksDetailsProvider, ConfigLinksDetailsContext };
